/**
 * Deskryptory kolumn rejestru VAT faktury — most między stałymi kolumnami rejestru
 * (`@/lib/documents/document-vat-register`) a kontraktem układu kolumn tabel osadzonych
 * (`@saas/shared` `embedded-table-columns`). Używają go: widok odczytu (`DocumentVatRegisterSection`),
 * edytor wierszy (`DocumentVatRegisterEditor`) i edytor układu na płótnie kreatora formatki
 * (`VatRegisterColumnsCanvas`) — jeden zapis `element.settings` dla odczytu i edycji.
 *
 * Kolumna `actions` (usuwanie wiersza) NIE jest kolumną układu — w edycji zawsze zostaje na końcu
 * tabeli, w dotychczasowej szerokości, więc deskryptora dla niej nie ma.
 *
 * Rejestr VAT ma kolumny stałe (bez pól dodatkowych), więc deskryptory zależą wyłącznie od `t`.
 */

import {
  type EmbeddedTableColumnDescriptor,
  type EmbeddedTableColumnKind,
  type ResolvedEmbeddedTableColumn,
  resolveEmbeddedTableColumns,
} from '@saas/shared';

import {
  type DocumentVatRegisterColumnId,
  VAT_REGISTER_COLUMN_HEADER_KEYS,
  VAT_REGISTER_SECTION_COLUMN_IDS,
} from '@/lib/documents/document-vat-register';

/** Kolumna układu rejestru VAT — każda kolumna rejestru oprócz `actions`. */
export type VatRegisterLayoutColumnId = Exclude<DocumentVatRegisterColumnId, 'actions'>;

/**
 * Kolumny układu w naturalnej kolejności edytora (bez `actions`) — ta sama lista co widok odczytu
 * (`VAT_REGISTER_SECTION_COLUMN_IDS`), więc bez zapisanego układu odczyt i edycja wyglądają jak dotąd.
 */
export const VAT_REGISTER_LAYOUT_COLUMN_IDS: readonly VatRegisterLayoutColumnId[] =
  VAT_REGISTER_SECTION_COLUMN_IDS.filter((id): id is VatRegisterLayoutColumnId => id !== 'actions');

/** Rodzaj danych kolumn (pełne pokrycie — nowa kolumna nie skompiluje się bez rodzaju). */
const COLUMN_KIND: Record<VatRegisterLayoutColumnId, EmbeddedTableColumnKind> = {
  lineNumber: 'integer',
  registerCode: 'text',
  categoryAbcd: 'text',
  taxCategory: 'text',
  vatRateCode: 'text',
  valueNet: 'decimal',
  valueVat: 'decimal',
  valueGross: 'decimal',
  valueNetCurrency: 'decimal',
  valueVatCurrency: 'decimal',
  valueGrossCurrency: 'decimal',
  service: 'boolean',
  period: 'date',
  ue: 'boolean',
  gtuCodes: 'text',
};

/**
 * Kolumny wymagane — nie da się ich ukryć: serwer odrzuca wpis rejestru bez rejestru, stawki VAT
 * i kwot netto/VAT/brutto (decyzja biznesowa).
 */
const REQUIRED_COLUMN_IDS: ReadonlySet<VatRegisterLayoutColumnId> = new Set([
  'registerCode',
  'vatRateCode',
  'valueNet',
  'valueVat',
  'valueGross',
]);

/** Kolumny zablokowane przez system — nie do ukrycia (numeracja porządkowa). */
const LOCKED_COLUMN_IDS: ReadonlySet<VatRegisterLayoutColumnId> = new Set(['lineNumber']);

/**
 * Deskryptory 15 kolumn rejestru VAT w naturalnej kolejności edytora (bez `actions`).
 *
 * Wyrównanie domyślne = reguła wg rodzaju (kwoty do prawej, reszta do lewej) — dokładnie jak dziś
 * w odczycie i w nagłówkach edycji; wyjątkiem jest `lineNumber` (liczba całkowita, ale dziś do lewej).
 *
 * Uwaga (kolumny logiczne `service`/`ue`, rodzaj `boolean`): logiczne wyrównanie kolumny — nagłówek i
 * odczyt — jest do lewej, ale w EDYCJI znaczniki (checkboxy) zostają domyślnie wyśrodkowane w
 * komórce, jak dotąd (`checkboxAlignClass` w `DocumentVatRegisterEditor`); dopiero jawne wyrównanie
 * z układu przesuwa znacznik zgodnie z ustawieniem.
 */
export function vatRegisterDescriptors(
  t: (key: string) => string,
): EmbeddedTableColumnDescriptor[] {
  return VAT_REGISTER_LAYOUT_COLUMN_IDS.map((id) => {
    const descriptor: EmbeddedTableColumnDescriptor = {
      id,
      label: t(VAT_REGISTER_COLUMN_HEADER_KEYS[id]),
      kind: COLUMN_KIND[id],
    };
    if (REQUIRED_COLUMN_IDS.has(id)) descriptor.required = true;
    if (LOCKED_COLUMN_IDS.has(id)) descriptor.locked = true;
    if (id === 'lineNumber') descriptor.defaultAlign = 'left';
    return descriptor;
  });
}

/** Kolumna widoczna po rozwiązaniu układu, z już zawężonym identyfikatorem kolumny rejestru. */
export interface VisibleVatRegisterColumn {
  colId: VatRegisterLayoutColumnId;
  col: ResolvedEmbeddedTableColumn;
}

/** Zawęża `id` kolumny układu do identyfikatora kolumny rejestru (bez `actions`); nieznane → `undefined`. */
export function asVatRegisterColumnId(id: string): VatRegisterLayoutColumnId | undefined {
  return VAT_REGISTER_LAYOUT_COLUMN_IDS.find((known) => known === id);
}

/**
 * Widoczne kolumny rejestru w kolejności układu. Brak `settings` = naturalna kolejność,
 * wszystko widoczne, zero nadpisań (tabela wygląda dokładnie jak przed układem kolumn);
 * kolumny wymagane i zablokowane są pokazane zawsze (reguła wspólna kontraktu).
 */
export function visibleVatRegisterColumns(
  t: (key: string) => string,
  settings: Record<string, unknown> | undefined,
): VisibleVatRegisterColumn[] {
  return resolveEmbeddedTableColumns(vatRegisterDescriptors(t), settings).flatMap((col) => {
    const colId = asVatRegisterColumnId(col.id);
    return col.visible && colId !== undefined ? [{ colId, col }] : [];
  });
}
